<template>
  <div class="card mb-5 mb-xxl-8">
    <div class="card mb-5 mb-xl-10" id="kt_profile_details_view">

      <!-- Header -->
      <div class="card-header flex-wrap border-0 pt-6 pb-0">
        <div class="card-title">
          <h3 class="card-label">Code-barres</h3>
        </div>
        <div class="card-toolbar">
          <button @click.prevent="printSelectedPDF" class="btn btn-primary" :disabled="isGenerating || !selectedIds.length">
            <span v-if="!isGenerating">
              <i class="bi bi-file-pdf me-1"></i>Générer PDF
              <span v-if="selectedIds.length" class="badge badge-light ms-1">{{ selectedIds.length }}</span>
            </span>
            <span v-else>
              <span class="spinner-border spinner-border-sm align-middle me-1"></span>Génération...
            </span>
          </button>
        </div>
      </div>

      <div class="card-body">

        <!-- Filtres -->
        <div class="d-flex flex-wrap gap-3 mb-5 align-items-center">
          <!-- Recherche -->
          <div class="position-relative">
            <i class="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-muted"></i>
            <input
              type="text"
              v-model="search"
              class="form-control form-control-solid ps-9"
              placeholder="Rechercher un article..."
              style="min-width: 240px;"
            />
          </div>

          <!-- Filtre catégorie -->
          <select v-model="selectedCategory" class="form-select form-select-solid" style="min-width: 200px;">
            <option value="">Toutes les catégories</option>
            <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
          </select>

          <!-- Filtre avec / sans code-barre -->
          <select v-model="filterBarcode" class="form-select form-select-solid" style="min-width: 190px;">
            <option value="">Tous les articles</option>
            <option value="with">Avec code-barre</option>
            <option value="without">Sans code-barre</option>
          </select>

          <!-- Compteur -->
          <span class="text-muted fs-7 ms-1">
            {{ filteredProducts.length }} article(s)
          </span>

          <!-- Actions de sélection rapide -->
          <button class="btn btn-sm btn-light-primary" @click="selectAllFiltered" :disabled="!filteredProducts.length">
            Tout sélectionner
          </button>
          <button v-if="selectedIds.length" class="btn btn-sm btn-light-danger" @click="selectedIds = []">
            Désélectionner ({{ selectedIds.length }})
          </button>
        </div>

        <!-- Tableau -->
        <div class="table-responsive table-scroll">
          <table class="table align-middle table-row-dashed fs-6 fw-bold gy-3">
            <thead>
              <tr class="text-start text-muted fw-bolder fs-7 text-uppercase gs-0">
                <th class="ps-2 w-40px">
                  <div class="form-check form-check-sm form-check-custom form-check-solid">
                    <input class="form-check-input" type="checkbox" @change="toggleAll" :checked="allSelected" :indeterminate.prop="someSelected" />
                  </div>
                </th>
                <th>Article</th>
                <th>Catégorie</th>
                <th>Code-barre</th>
                <th style="width: 110px;">Copies</th>
              </tr>
            </thead>
            <tbody class="text-gray-600">
              <tr v-if="!filteredProducts.length">
                <td colspan="5" class="text-center text-muted py-6">Aucun article trouvé</td>
              </tr>
              <tr v-for="product in filteredProducts" :key="product.id">
                <td class="ps-2">
                  <div class="form-check form-check-sm form-check-custom form-check-solid">
                    <input class="form-check-input" type="checkbox" v-model="selectedIds" :value="product.id" />
                  </div>
                </td>
                <td>{{ product.name }}</td>
                <td>
                  <span class="badge badge-light-info">{{ product.category_name }}</span>
                </td>
                <td>
                  <img v-if="product.barcode && product.barcode_url"
                       :src="product.barcode_url"
                       style="max-width: 140px; height: 32px; display: block;" />
                  <span v-else class="text-muted fst-italic">—</span>
                </td>
                <td>
                  <input
                    style="width: 80px!important;"
                    class="form-control form-control-sm"
                    type="number"
                    min="1"
                    v-model.number="formPrint[product.id].copies"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'Barcode',
  data() {
    return {
      products: [],
      formPrint: {},
      selectedIds: [],
      isGenerating: false,
      search: '',
      selectedCategory: '',
      filterBarcode: '',
    }
  },
  computed: {
    categories() {
      const map = {}
      this.products.forEach(p => {
        if (p.category_id && !map[p.category_id]) {
          map[p.category_id] = { id: p.category_id, name: p.category_name }
        }
      })
      return Object.values(map).sort((a, b) => a.name.localeCompare(b.name))
    },

    filteredProducts() {
      return this.products.filter(p => {
        const matchesSearch = !this.search ||
          p.name.toLowerCase().includes(this.search.toLowerCase())
        const matchesCategory = !this.selectedCategory ||
          p.category_id === this.selectedCategory
        const matchesBarcode =
          !this.filterBarcode ||
          (this.filterBarcode === 'with' && p.barcode) ||
          (this.filterBarcode === 'without' && !p.barcode)
        return matchesSearch && matchesCategory && matchesBarcode
      })
    },

    allSelected() {
      return this.filteredProducts.length > 0 &&
        this.filteredProducts.every(p => this.selectedIds.includes(p.id))
    },

    someSelected() {
      return this.selectedIds.some(id => this.filteredProducts.find(p => p.id === id)) && !this.allSelected
    },
  },

  async mounted() {
    await this.getProducts()
  },

  watch: {
    '$route.params.id'(newId, oldId) {
      // Vue Router réutilise ce composant quand on passe de la fiche d'une
      // boutique à une autre (même route, seul :id change) — sans ce watcher,
      // la liste garde les produits de l'ancienne boutique consultée.
      if (newId && newId !== oldId) this.getProducts()
    },
  },

  methods: {
    ...mapActions('products', ['downloadBarcodePDF']),
    ...mapActions('shop', ['getShopProductsList']),

    toggleAll(e) {
      const filteredIds = this.filteredProducts.map(p => p.id)
      if (e.target.checked) {
        this.selectedIds = [...new Set([...this.selectedIds, ...filteredIds])]
      } else {
        const filteredSet = new Set(filteredIds)
        this.selectedIds = this.selectedIds.filter(id => !filteredSet.has(id))
      }
    },

    selectAllFiltered() {
      const filteredIds = this.filteredProducts.map(p => p.id)
      this.selectedIds = [...new Set([...this.selectedIds, ...filteredIds])]
    },

    async getProducts() {
      try {
        const response = await this.getShopProductsList(this.$route.params.id)
        this.products = response.data.shopProducts ?? []
        const prints = {}
        this.products.forEach(p => { prints[p.id] = { copies: 1 } })
        this.formPrint = prints
      } catch (err) {
        window.toastr['error']('Erreur lors du chargement des produits')
      }
    },

    async printSelectedPDF() {
      if (!this.selectedIds.length) {
        window.toastr['warning']('Sélectionnez au moins un produit')
        return
      }

      this.isGenerating = true
      try {
        const payload = this.selectedIds.map(id => ({
          id,
          copies: parseInt(this.formPrint[id]?.copies) || 1,
        }))

        await this.downloadBarcodePDF({ product_sale_elements: payload })
        window.toastr['success']('PDF généré avec succès')
      } catch (err) {
        window.toastr['error']('Erreur lors de la génération du PDF')
      } finally {
        this.isGenerating = false
      }
    },
  },
}
</script>

<style scoped>
.table-scroll {
  max-height: 60vh;
  overflow-y: auto;
}

.table-scroll thead tr th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  box-shadow: 0 1px 0 #e4e6ef;
}

.table-scroll::-webkit-scrollbar {
  width: 5px;
}

.table-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 8px;
}

.table-scroll::-webkit-scrollbar-thumb {
  background: #b5b5c3;
  border-radius: 8px;
}

.table-scroll::-webkit-scrollbar-thumb:hover {
  background: #7e8299;
}
</style>
